/**
 * Rubric phía sinh viên (plan 2026-10-03 §3):
 * - `RubricPreview`: xem tiêu chí chấm trong lúc làm bài (nếu GV cho xem).
 * - `RubricResult`: bảng điểm theo tiêu chí sau khi GV chấm — điểm hiển thị thang 10
 *   qua `ScoreTag` (CLAUDE.md §13), kèm mức đạt + nhận xét từng tiêu chí.
 */
import { Collapse, Table, Tag, Typography } from "antd";
import type { RubricAssessment, RubricStructure } from "@/admin/api/rubrics";
import { ScoreTag, pctTo10 } from "@/components/ScoreTag";

const { Text } = Typography;

export function RubricPreview({ rubric }: { rubric: RubricStructure }) {
  return (
    <Collapse
      size="small"
      style={{ marginTop: 28 }}
      items={[
        {
          key: "rubric",
          label: `Tiêu chí chấm (rubric): ${rubric.title}`,
          children: (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={rubric.criteria}
              columns={[
                {
                  title: "Tiêu chí",
                  width: 200,
                  render: (_, c) => (
                    <>
                      <Text strong>{c.title}</Text>
                      {c.description && (
                        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                          {c.description}
                        </Text>
                      )}
                    </>
                  ),
                },
                {
                  title: "Các mức",
                  render: (_, c) =>
                    c.levels.map((l) => (
                      <div key={l.id} style={{ marginBottom: 4 }}>
                        <Tag>{l.label}</Tag>
                        {l.descriptor && <Text type="secondary">{l.descriptor}</Text>}
                      </div>
                    )),
                },
              ]}
            />
          ),
        },
      ]}
    />
  );
}

export function RubricResult({
  rubric,
  result,
  superseded = false,
}: {
  rubric: RubricStructure;
  result: RubricAssessment;
  /** Phúc khảo đã đổi điểm → bảng này là lần chấm ban đầu. */
  superseded?: boolean;
}) {
  const rows = rubric.criteria.map((c) => {
    const item = result.items.find((it) => it.criterion === c.id);
    const level = c.levels.find((l) => l.id === item?.level);
    return { criterion: c, item, level };
  });
  return (
    <div style={{ marginTop: 12 }}>
      <Text type="secondary">
        {superseded ? "Chấm ban đầu theo rubric" : "Chấm theo rubric"} “{rubric.title}”
        {superseded ? " (trước phúc khảo)" : ""} — điểm câu:{" "}
        <ScoreTag
          value={result.max_points ? pctTo10((result.total_points / result.max_points) * 100) : 0}
        />
      </Text>
      <Table
        style={{ marginTop: 8 }}
        size="small"
        rowKey={(r) => r.criterion.id}
        pagination={false}
        dataSource={rows}
        columns={[
          { title: "Tiêu chí", render: (_, r) => <Text strong>{r.criterion.title}</Text> },
          {
            title: "Mức đạt",
            width: 160,
            render: (_, r) => (r.level ? <Tag color="blue">{r.level.label}</Tag> : "—"),
          },
          {
            title: "Điểm",
            width: 80,
            render: (_, r) => (
              <ScoreTag
                value={
                  r.criterion.max_points
                    ? pctTo10(((r.item?.points ?? 0) / r.criterion.max_points) * 100)
                    : 0
                }
              />
            ),
          },
          {
            title: "Nhận xét",
            render: (_, r) => r.item?.comment || <Text type="secondary">—</Text>,
          },
        ]}
      />
    </div>
  );
}
